import { z } from 'zod'
import type { XYZ } from './bot'

/**
 * Формат кадров камеры (docs/08). Живёт в `shared`, потому что кадр собирает
 * `apps/runner`, маршрутизирует `apps/api`, а разбирает `@apiary/viewer-core`.
 *
 * ### Почему кадр не `column.toJson()`, как в prismarine-viewer
 *
 * Штатный `WorldView` отдаёт колонку сериализованным `prismarine-chunk`-ом, и
 * браузеру пришлось бы держать `prismarine-chunk` + `minecraft-data`, чтобы её
 * прочитать. Это десятки мегабайт бандла (официальная сборка prismarine-viewer —
 * 63 МБ worker.js) и, что важнее, **чужие stateId**: наши id приходят из
 * форка minecraft-data для 26.2, а ассеты рендерятся по 1.21.11 (ADR-11), где
 * нумерация состояний другая.
 *
 * Поэтому версионно-зависимую часть делает runner (у него есть настоящий
 * minecraft-data): он разворачивает колонку в палитру «имя + свойства + флаги»
 * и индексы. Браузер получает данные, которые читаются без minecraft-data вовсе.
 */

/** Флаги блока в палитре: то, что мешеру нужно знать про соседа. */
export const VIEWER_BLOCK_FLAG = {
  /** `block.transparent` — сквозь блок видно, соседняя грань не отсекается. */
  transparent: 1,
  /** Форма блока — полный куб 0..1 (у prismarine-viewer это `isCube`). */
  cube: 2,
  /** Растение: вода рядом с ним не рисует грань. */
  plant: 4
} as const

/**
 * Запись палитры колонки. Имена полей однобуквенные сознательно: запись
 * повторяется десятки раз на колонку и сотни раз на кадр, а msgpack пишет
 * ключи объектов строками — длинные имена здесь стоят реального трафика.
 */
export interface ViewerPaletteEntry {
  /** Имя блока без пространства имён: `oak_stairs`. */
  n: string
  /** Числовой id блока: мешер сравнивает по нему соседей одного типа. */
  t: number
  /** metadata — для воды и лавы это уровень жидкости. */
  m: number
  /** Свойства состояния в виде `facing=east,half=bottom`; пусто — свойств нет. */
  p: string
  /** Битовая маска `VIEWER_BLOCK_FLAG`. */
  f: number
}

/** Секция 16×16×16. Пустые (только воздух) в кадр не попадают вообще. */
export interface ViewerSectionFrame {
  /** Мировая Y нижнего слоя секции. */
  y: number
  /** Ширина индекса в байтах: 1 или 2 (little-endian). */
  w: 1 | 2
  /** Палитра секции: индексы в палитре колонки. */
  pal: number[]
  /** 4096 индексов в `pal`, порядок `y → z → x`, упакованных по `w` байт. */
  idx: Uint8Array
}

/** Кадр `viewer.chunk`: одна колонка мира. */
export interface ViewerChunkFrame {
  /** Мировые координаты угла колонки в блоках (как их отдаёт `WorldView`). */
  x: number
  z: number
  /** Низ мира и его высота — у 26.x это `-64` и `384`. */
  minY: number
  height: number
  /** Биом в центре колонки: по нему мешер тонирует траву, листву и воду. */
  biome: string
  palette: ViewerPaletteEntry[]
  sections: ViewerSectionFrame[]
}

/** Кадр `viewer.unloadChunk`. */
export interface ViewerUnloadChunkFrame {
  x: number
  z: number
}

/**
 * Кадр `viewer.blockUpdate`. Вместо `stateId` едет запись палитры: у браузера
 * нет таблицы состояний (см. комментарий к модулю).
 */
export interface ViewerBlockUpdateFrame {
  pos: XYZ
  block: ViewerPaletteEntry
}

/** Кадр `viewer.position`: позиция и взгляд бота, 10 Гц. */
export interface ViewerPositionFrame {
  pos: XYZ
  yaw: number
  pitch: number
  /** Признак «пора догрузить меши» — используется клиентом для интерполяции. */
  addMesh: boolean
}

// ─────────────────────────── сущности рядом с ботом ───────────────────────────

/**
 * Кадр `viewer.entities` — снимок окрестностей бота (FR-V3).
 *
 * ### Почему снимок, а не поток событий про каждую сущность
 *
 * Штатный `WorldView` пересылает события mineflayer по одной сущности
 * (`entitySpawn`/`entityMoved`/`entityGone`). Так клиент собирает состояние
 * инкрементально, и любая потеря события оставляет в сцене «призрака» — модель
 * игрока, который давно ушёл, — навсегда: следующего события про него уже не
 * будет. Плюс инкрементальный поток нельзя ограничить ни радиусом, ни числом:
 * на людном спавне это сотни сообщений в секунду.
 *
 * Поэтому кадр — **полное** состояние: ближайшие `VIEWER_ENTITY_LIMIT`
 * сущностей в радиусе `VIEWER_ENTITY_RADIUS`, 5 Гц. Клиент удаляет всех, кого в
 * кадре нет, и лечится сам за 200 мс. Цена — повтор одних и тех же полей;
 * поэтому ключи однобуквенные, углы байтовые, а числа едут float32
 * (см. `HEAVY_FRAME_KINDS` в runner'е).
 */

/** Радиус кадра в блоках: дальше сущность занимает пару пикселей при vd 4. */
export const VIEWER_ENTITY_RADIUS = 32
/**
 * Сколько сущностей влезает в кадр, считая самого бота. Обрезаются дальние:
 * ближние и есть то, на что смотрит наблюдатель, а бюджет docs/08 §5 не даёт
 * гнать людный спавн целиком.
 */
export const VIEWER_ENTITY_LIMIT = 40
/**
 * Период кадра — 5 Гц. Позиции самого бота нужна плавность (10 Гц), а чужие
 * модели интерполируются на клиенте: 200 мс на кадр незаметны, а трафик вдвое
 * меньше.
 */
export const VIEWER_ENTITY_INTERVAL_MS = 200

/**
 * Класс сущности — он же выбор геометрии на клиенте.
 *
 * Раньше классов было три (игрок / моб / «всё прочее»), и в «прочее» валились
 * вагонетка, лодка, стрела, шар опыта, брошенный предмет и любая сущность, тип
 * которой не нашёлся в реестре. Все они рисовались одной и той же жёлтой
 * полупрозрачной коробкой по хитбоксу: вагонетка 0.98×0.7 выглядела бледным
 * квадратиком на рельсах, а сущность с пустыми габаритами — кубиком 0.25.
 * Отличить одно от другого было нельзя, и пользователь честно сообщил, что
 * «вагонетки нет».
 *
 * Дробление класса не стоит НИ ОДНОГО байта: поле `c` в кадре и так есть, а
 * msgpack пишет любое число 0..127 одним байтом. Поэтому лечим именно классом,
 * а не новыми полями (кадр идёт 5 Гц и умножается на число сущностей).
 */
export const VIEWER_ENTITY_CLASS = {
  player: 0,
  /** Живое: моб, животное, житель, стойка для брони. Коробка по хитбоксу. */
  mob: 1,
  /**
   * Мелочь и реквизит: брошенный предмет, рамка, картина, TNT, служебные
   * сущности сервера (маркеры, display-голограммы). Маленький кубик.
   */
  object: 2,
  /** Транспорт: вагонетка, лодка, плот. Открытая сверху коробка-кузов. */
  vehicle: 3,
  /** Снаряд: стрела, поплавок, снежок, жемчуг. Маленький вытянутый дротик. */
  projectile: 4,
  /** Шар опыта. Маленький шарик — единственная круглая сущность в ванили. */
  orb: 5,
  /**
   * Реестр не знает такой сущности.
   *
   * Отдельный класс, а не «свалить в object»: на 26.2 мы играем данными 26.1
   * (см. dataPaths minecraft-data), и при сдвиге id mineflayer уводит сущность в
   * ветку «unknown entity» с пустыми габаритами. Молча рисовать её кубиком —
   * значит прятать поломку совместимости; нейтральная серая коробка честно
   * говорит «это что-то есть, но чем оно является — неизвестно».
   */
  unknown: 6
} as const
export type ViewerEntityClass = (typeof VIEWER_ENTITY_CLASS)[keyof typeof VIEWER_ENTITY_CLASS]

/**
 * Состояние сущности, битовая маска. Здесь только то, что достаётся из
 * mineflayer даром — из `entity.onGround` и байта флагов в метаданных. Позы
 * (сон, плавание на животе) требуют разбора POSE-поля и в кадр не едут.
 */
export const VIEWER_ENTITY_FLAG = {
  onGround: 1,
  inWater: 2,
  /** Сидит/крадётся — в модели это опущенная на 2 пикселя голова. */
  crouching: 4,
  sprinting: 8,
  /**
   * Сам бот. Нужен флаг, а не сравнение id: в режиме «орбита» модель бота
   * рисуется, в первом лице её надо спрятать, иначе камера смотрит изнутри
   * собственной головы.
   */
  self: 16,
  /**
   * Враждебный моб (`category === 'Hostile mobs'` в minecraft-data).
   *
   * Бит, а не имя вида в кадре: подписи над мобами не рисуются (ник — только у
   * игрока), значит различать корову и зомби наблюдателю нечем, кроме цвета.
   * Флаг влезает в тот же байт `f` — на проводе он бесплатен, а имя вида стоило
   * бы ~8 байт на каждого моба в каждом кадре.
   */
  hostile: 32
} as const

/** Шагов в полном обороте: 1 байт = 1/256 оборота — так углы шлёт и сам MC. */
export const VIEWER_ANGLE_STEPS = 256

/** Радианы → байт угла. Небезопасное число превращается в 0, а не в NaN на проводе. */
export function viewerAngleToByte(radians: number): number {
  if (!Number.isFinite(radians)) return 0
  return Math.round((radians / (2 * Math.PI)) * VIEWER_ANGLE_STEPS) & (VIEWER_ANGLE_STEPS - 1)
}

/** Байт угла → радианы (0..2π). Обратное `viewerAngleToByte` с точностью 1.4°. */
export function viewerAngleFromByte(byte: number): number {
  if (!Number.isFinite(byte)) return 0
  return ((byte & (VIEWER_ANGLE_STEPS - 1)) * (2 * Math.PI)) / VIEWER_ANGLE_STEPS
}

/**
 * Одна сущность в кадре. Однобуквенные ключи — по той же причине, что в палитре
 * колонки: запись повторяется 40 раз в кадре и 5 раз в секунду, а msgpack пишет
 * ключи объектов строками.
 */
export interface ViewerEntity {
  /** `entityId` протокола: стабильный ключ сущности между кадрами. */
  i: number
  /** Класс из `VIEWER_ENTITY_CLASS`. */
  c: ViewerEntityClass
  /** Позиция ног в мировых координатах. */
  x: number
  y: number
  z: number
  /** yaw тела в байтах угла (`viewerAngleToByte`). */
  a: number
  /** pitch головы в байтах угла. */
  p: number
  /** Битовая маска `VIEWER_ENTITY_FLAG`. */
  f: number
  /**
   * Ник игрока или имя вида (`zombie`, `chest_minecart`, `unknown`).
   *
   * Едет только у классов `player`, `mob`, `vehicle` и `unknown`, то есть там,
   * где это либо видно наблюдателю (ник игрока), либо меняет картинку
   * (`boat`/`raft` красятся деревом, вагонетка — металлом), либо диагностирует
   * поломку (`unknown`). У `object`, `projectile` и `orb` имени нет: их в кадре
   * бывают десятки, подпись над ними не рисуется никогда (поплавок удочки с
   * ником над головой — то, из-за чего это правило и появилось), а ~8 байт ×
   * десятки × 5 Гц — уже заметный трафик.
   *
   * ВАЖНО: наличие `n` НЕ означает подпись в 3D. Подпись рисуется только
   * игрокам (`hasNametag` в viewer-core).
   */
  n?: string
  /**
   * UUID игрока без дефисов — ключ скина (`/api/skins/:uuid.png`). У мобов и
   * предметов отсутствует; у offline-игроков может отсутствовать, тогда прокси
   * отдаёт дефолтный силуэт по `offline`.
   */
  u?: string
  /**
   * Габариты хитбокса — только у не-игроков: их силуэт рисуется по ним.
   * У игрока модель стандартных пропорций, и 8 байт на кадр тут лишние.
   *
   * У не-игрока присутствуют ВСЕГДА и всегда положительны: runner подставляет
   * значения по классу, когда реестр отдал ноль или ничего. Клиент не должен
   * додумывать размер сам — по классу «объект» не отличить стрелу от лодки.
   */
  w?: number
  h?: number
}

/** Кадр `viewer.entities`: ближайшие сущности, отсортированные по расстоянию. */
export interface ViewerEntitiesFrame {
  /** Бот всегда первый — даже когда лимит выбран целиком (флаг `self`). */
  e: ViewerEntity[]
}

// ───────────────────────────── мини-карта (L1) ─────────────────────────────

/** Сторона квадрата мини-карты в блоках (docs/08 §4: 64×64). */
export const MINIMAP_SIZE = 64
/** Байт на клетку: высота (u8) + индекс палитры (u8). */
export const MINIMAP_BYTES_PER_CELL = 2
export const MINIMAP_BYTES = MINIMAP_SIZE * MINIMAP_SIZE * MINIMAP_BYTES_PER_CELL
/** Период пересчёта срезa высот (docs/10 §4: 0.5 Гц). */
export const MINIMAP_INTERVAL_MS = 2_000

/**
 * Второй байт клетки — индекс в `MINIMAP_COLOR_TABLE`
 * (`minimap-palette.generated.ts`), то есть цвет КОНКРЕТНОГО блока.
 *
 * Раньше здесь была палитра из 11 категорий («трава», «камень», «дерево»…), и
 * всё за их пределами красилось серым: бетон, шерсть, терракота и кирпич на
 * карте сливались с камнем, постройки не читались. Байт вмещает 256 значений,
 * поэтому переход на цвет по блоку не поменял формат кадра — он остался
 * 64×64×2 = 8 КБ.
 *
 * `0` — «нет данных» (колонка не прогружена): клиент рисует такую клетку почти
 * прозрачной, чтобы дырка в карте была честно видна.
 */
export const MINIMAP_COLOR_NONE = 0

/** Кадр `viewer.minimap`: срез высот вокруг бота. */
export interface MinimapFrame {
  botId: string
  /** Мировые координаты левого верхнего угла среза. */
  originX: number
  originZ: number
  /** Y бота: высоты в байтах закодированы как `top - baseY + 128`. */
  baseY: number
  /** `MINIMAP_BYTES` байт: пары (высота, индекс цвета в `MINIMAP_COLOR_TABLE`). */
  data: Uint8Array
}

// ─────────────────────────────── ассеты ───────────────────────────────

/**
 * Версия ассетов рендера для версии Minecraft (ADR-11).
 *
 * `minecraft-assets` знает данные до 1.21.11, для 26.x их нет, поэтому любая
 * 26.x рендерится набором 1.21.11: неизвестные блоки честно уходят в
 * `missing_texture`.
 */
export const VIEWER_ASSETS_FALLBACK_VERSION = '1.21.11'

export function viewerAssetsVersion(mcVersion: string | null | undefined): string {
  if (!mcVersion) return VIEWER_ASSETS_FALLBACK_VERSION
  // Пока апстрим не знает 26.x, единственный набор ассетов — фоллбэк.
  return VIEWER_ASSETS_FALLBACK_VERSION
}

// ─────────────────────────── лимиты и параметры ───────────────────────────

/** docs/06 §6 и docs/08 §5: 2..8, по умолчанию 4. */
export const VIEW_DISTANCE_MIN = 2
export const VIEW_DISTANCE_MAX = 8
export const VIEW_DISTANCE_DEFAULT = 4
/** docs/10 §7: на мобиле трафик дорогой — открываем на минимуме. */
export const VIEW_DISTANCE_MOBILE = 2

export const ViewDistance = z
  .number()
  .int()
  .min(VIEW_DISTANCE_MIN)
  .max(VIEW_DISTANCE_MAX)
  .default(VIEW_DISTANCE_DEFAULT)

/** docs/08 §5: без heartbeat 30 с сессия закрывается. */
export const VIEWER_HEARTBEAT_TIMEOUT_MS = 30_000
/** Клиент шлёт heartbeat втрое чаще таймаута — один потерянный кадр не убивает сессию. */
export const VIEWER_HEARTBEAT_INTERVAL_MS = 10_000
/** Как часто API проверяет просроченные сессии. */
export const VIEWER_SWEEP_INTERVAL_MS = 5_000
/**
 * Сколько времени после открытия сессия живёт без подписчиков.
 *
 * Правило docs/08 §3.2 «в комнате нет сокетов → закрываем» само по себе делает
 * камеру неоткрываемой: между ответом `POST /bots/:id/viewer` и `subscribe` по
 * WS проходит круговая задержка, а runner начинает сыпать чанки сразу по
 * `viewerOpen`. Поэтому первые секунды пустая комната — норма; дальше сессия
 * закрывается с `no-subscribers`.
 */
export const VIEWER_SUBSCRIBE_GRACE_MS = 10_000
/**
 * Сколько сервер ждёт `viewer.ready` от клиента, прежде чем грузить мир сам.
 *
 * Нужно только ради вкладки со старым бандлом, которая про это событие не знает:
 * для неё поведение остаётся прежним (мир поедет сразу после входа в комнату),
 * а свежий клиент присылает сигнал за доли секунды и до таймера не доходит.
 */
export const VIEWER_READY_FALLBACK_MS = 4_000
/** Высота глаз игрока — камера в режиме «первое лицо» (docs/08 §3.3). */
export const VIEWER_EYE_HEIGHT = 1.62
